import type { Season } from '@/types/season';
import { toLocalISODate } from '@/utils/date-utils';

/**
 * Default-season selection ladder for the Prep tab.
 *
 * Rule (in priority order):
 *  1. Season with `isPrimary === true`, if any.
 *  2. Nearest future opener (openerDate >= today).
 *  3. Most recently ended season (latest closerDate < today).
 *  4. null when no seasons exist.
 */
export function pickDefaultSeason(seasons: Season[], today: Date): Season | null {
  if (seasons.length === 0) return null;

  // 1. Primary flag
  const primary = seasons.find((s) => s.isPrimary === true);
  if (primary) return primary;

  const todayStr = toLocalISODate(today);

  // 2. Nearest future opener
  const upcoming = seasons
    .filter((s) => s.openerDate >= todayStr)
    .sort((a, b) => a.openerDate.localeCompare(b.openerDate));
  if (upcoming.length > 0) return upcoming[0];

  // 3. Most recently ended (latest closerDate before today)
  const past = seasons
    .filter((s) => s.closerDate !== undefined && s.closerDate < todayStr)
    .sort((a, b) => {
      // SAFETY: both closerDate values are defined due to filter above
      return b.closerDate!.localeCompare(a.closerDate!);
    });
  if (past.length > 0) return past[0];

  // 4. No match — seasons exist but none qualify via steps 1–3 (e.g. past opener,
  // no closerDate set). Return null so the caller shows the onboarding empty-state
  // rather than silently picking an arbitrary season.
  return null;
}
